<template>

    <div class="SavingOptionsTable">

        <McpWheelLoader
            v-if="displayLoader"
            :display="displayLoader"
        />

        <!-- Only Mobile -->
        <div class="mobile-infos container">
            <!-- Annual consumption -->
            <div class="mobile-infos__annual-consumption">
                <svg v-svg symbol="Consumption" size="0 0 25 25" role="presentation" class="icon--inline"></svg>
                <p>
                    Votre consommation actuelle annuelle <br>
                    <span>(estimation)</span>
                </p>

                <div>{{ addThousandSeparationToNumber(dwellingResumeDatas.energy_bill_amount) }}€</div>
            </div>
            
            <!-- Dpe label -->
            <div class="mobile-infos__dpe-label">
                <svg v-svg symbol="Dpe" size="0 0 25 25" role="presentation" class="icon--inline"></svg>
                <p>
                    La classe énergétique actuelle estimée de votre logement est : <br> 
                </p>
                <div :class="`color-${dpeLabelColor}`">{{this.mcpDpe}}</div>
            </div>
        </div>

        <div class="mobile-infos mobile-title">Nos recommandations</div>

        <!-- Only Desktop -->
        <div class="current-consumption desktop-infos">
            <p>
                Votre consommation actuelle annuelle <br>
                <span>(estimation)</span>
            </p>

            <div>{{ addThousandSeparationToNumber(dwellingResumeDatas.energy_bill_amount) }}€</div>

            <div class="picto">
                <img :src="`${mediaBaseUrl}simulator/Layer_1.png`"  alt="picto energy"/>
            </div>
        </div>

        <div class="recommendations-table">
            <!-- table Head -->
            <div class="recommendations-table__t-head desktop-infos">
                <div>Équipements conseillés</div>
                <div>Empreinte carbone annuelle économisée <span>(estimation)</span></div>
                <div>Économies annuelle potentielles <span>(estimation)</span></div>
            </div>

            <!-- table Body -->
            <div class="recommendations-table__t-body">
                <div
                    v-for="equipment in dwellingResumeDatas.recommended_equipments" :key="equipment.equipment_name"
                    class="t-row" 
                >
                    <!-- col name -->
                    <div class="t-row__equipment-name">{{equipment.equipment_name}}</div>

                    <!-- col carbon -->
                    <div class="t-row__carbon-footprint" :class="{negative: equipment.carbon_footprint_saved.toString().includes('+')}">
                        <p class="mobile-infos">Emprunte carbone économisée</p>
                        {{ equipment.carbon_footprint_saved }} gCo²/kwh
                    </div>
                    
                    <!-- col saving -->
                    <div class="t-row__potential-saving" :class="{negative: equipment.potential_saving.toString().includes('+')}">
                        <p class="mobile-infos">Économie potentielle (estimation)</p>
                        {{ addThousandSeparationToNumber(equipment.potential_saving) }}€
                    </div>
                    
                    <!-- col actions -->
                    <div class="t-row__actions">
                        <div class="btn_container">
                            <button
                                v-if="equipment.aids_calculations && equipment.aids_max_amount > 0"
                                class="btn-actions btn-actions__blue"
                                @click="submitLead(equipment, 'Calcul Aides')"
                            >
                                Calculez mes aides
                            </button>
                            <p v-if="equipment.aids_max_amount && equipment.aids_calculations" class="aidsMaxAmount">Aides jusqu'à {{ equipment.aids_max_amount }}€</p>
                        </div>
                        
                        <div class="btn_container">
                            <button
                                class="btn-actions btn-actions__red"
                                @click="submitLead(equipment, 'Rdv')"
                            >
                                Prendre RDV
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>

</template>

<script>
import EventBus from "@/event-bus";

import McpWheelLoader from '@/components/Loaders/McpWheelLoader.vue';
import addItemToCartMixin from "@/mixins/actions/cart/addItemToCartMixin.js";
import tunnelFormMixin from "@/mixins/actions/simulator/tunnelFormMixin";

import { addThousandSeparationToNumber } from "@/services/tools.js";
import { mapActions, mapGetters } from "vuex";

export default {
    name: "SavingOptionsTable",

    mixins: [ addItemToCartMixin, tunnelFormMixin],
    
    computed: {
        ...mapGetters({ 
            dwellingResumeDatas: "getDwellingStepResume",
            simulatorInfos: "getSimulatorPathInfos",
            leadConfig: "getLeadConfig",
            mcpDpe: "getMcpDpe"
        }),
    },

    props: {
        dpeLabelColor: {
            type: String,
            default: 'red'
        }
    },

    data() {
        return {
            displayLoader: false,
            mediaBaseUrl: `${process.env.VUE_APP_API_BASE_URL}/skin/frontend/mcp/default/images/`,
            hasAgencyEligible: false,
            isMobileSize: false
        };
    },

    components: { 
        McpWheelLoader,
     },

    methods: {
        async submitLead(equipment, lastAction) {
            //Store answers
            this.setAnswerDatas({key: 'mcp_customer_heating_equipment', answer: equipment.equipment_value, form: 'saving'});
            this.setAnswerDatas({key: 'mcp_customer_heating_equipment_name', answer: equipment.equipment_name, form: 'saving'});
            this.setAnswerDatas({key: 'potential_saving', answer: equipment.potential_saving, form: 'saving'});
            this.setAnswerDatas({key: 'last_action', answer: lastAction, form: 'saving'});
            this.setAnswerDatas({key: 'isAidsEligible', answer: equipment.aids_calculations, form: 'saving'})

            this.setAnswerDatas({key:'heat_loss', answer:this.dwellingResumeDatas.heat_loss, form:'saving'});
                
            try {
                this.displayLoader = true;

                // cart update with correct lead product
                if(equipment.lead_product_id){
                    await this.addItemToCart(equipment.lead_product_id, 1, true);
                }

                // eligibility
                this.leadConfig.category = equipment.category_id;
                this.hasAgencyEligible = await this._isPostalCodeEligible(this.leadFormAnswers.postalcode);

                // create lead
                let response = await this.doSaveLead({step: `?step_number=2&last_action=${lastAction}`, saveLead: true});
                
                if(lastAction == "Rdv") EventBus.$emit('gtm-proposition_rdv::push');
                this.displayLoader = false;

                if(response.error) {
                    //If on update, no agency available for new universe
                    if(response.error.error == 'No agency available') {
                       EventBus.$emit("simulator-page::exit", "zipcode_failure"); 
                    } else {
                        console.error('submitLead error', response.error)
                        
                        this.$fire({
                            title: "Oups",
                            text: "Une erreur est survenu, veuillez réessayer plus tard",
                            type: "error",
                            timer: 1500,
                            position: "top-end",
                        });
                    }
                    
                } else {
                    lastAction === "Rdv" ? this.rdvAction() :this.aidsAction();
                }

            } catch(error) {
                console.error(error)
                this.displayLoader = false;
                
                this.$fire({
                    title: "Oups",
                    text: "Une erreur est survenu, veuillez réessayer plus tard",
                    type: "error",
                    timer: 1500,
                    position: "top-end",
                });
            }
            
        },

        rdvAction() {
            
            if(!this.simulatorInfos.is_low_dpe_eligible_to_aids && this.simulatorInfos.overall_renovation_contact) {
                EventBus.$emit("simulator-page::exit", "midStep");
            } else if(this.hasAgencyEligible){
                EventBus.$emit("stepAction::validate", 'save_energy');
                EventBus.$emit('stepAction::current', 'final_step');
                EventBus.$emit("simulator-page::resume");
            } 
        },

        aidsAction() {
            if(this.hasAgencyEligible) {
                EventBus.$emit("stepAction::validate", 'save_energy');
                EventBus.$emit("simulator-page::tunnel", 'aids');
            }
        },

        ...mapActions([
            "setAnswerDatas",
            "doSaveLead"
        ]),

        //Imported functions
        addThousandSeparationToNumber
    },

    mounted() {
        EventBus.$emit('gtm-proposition::push');
    },
    
}
</script>

<style lang="scss">

    .SavingOptionsTable {

        .mobile-infos {

            &.container {
                display: flex;
                margin: 20px 0;

                .mobile-infos__annual-consumption{
                    position: relative;
                    width: 50%;
                    padding-right: 20px;

                    div {
                        font-size: 2.5rem;
                        color: $c-mcp-red;
                    }

                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        right: 0;
                        height: 100%;
                        width: 1px;
                        background: $c-grey-5;
                    }
                }

                .mobile-infos__dpe-label {
                    width: 50%;
                    padding-left: 20px;

                    div {
                        font-size: 2.5rem;

                        //Dpe label colors
                        &.color-green {
                            color: $c-mcp-green;
                        }

                        &.color-orange {
                            color: $color-orange;
                        }

                        &.color-red {
                            color: $c-mib-main;
                        }
                    }
                }
            }
        }

        .current-consumption {
            display: flex;
            justify-content: space-around;
            padding: 1vw;
            
            margin-bottom: 20px;
            border: 2px solid $c-grey-5;
            border-radius: 10px;

            div.picto {
                display: none;
            }

            div {
                font-size: 3rem;
                line-height: 4.6rem;
                color: $c-mcp-red;
                min-width: 50%;
            }
        }

        .recommendations-table {
            flex: 1;
            border-radius: 10px;
            

            
            &__t-head,
            &__t-body .t-row {
                display: grid;
                text-align: center;
            }

                &__t-head {
                    font-family: $font-family-title;
                    padding: 30px 30px;
                    border-radius: 10px;
                    background-color: $c-grey-bg2;
                }

                &__t-body {
                .t-row {
                    
                    padding: 30px 0;
                    margin: 0 15px;
                    font-size: 2.2rem;
                    line-height: 2.5rem;

                    &__carbon-footprint {
                        color: $c-blue-simu;

                        p {
                            font-size: 1.4rem;
                            color: initial;
                        }
                    }
                    &__carbon-footprint.negative {
                        color: $c-mcp-red;
                    }

                    &__potential-saving {
                        font-size: 3rem;
                        line-height: 3.5rem;
                        color: $c-mcp-green;

                        p {
                            font-size: 1.4rem;
                            color: initial;
                        }
                    }

                    &__potential-saving.negative {
                        color: $c-mcp-red;
                    }

                    &__actions {
                        font-size: 1.2rem;

                        .btn_container {
                            position: relative;
                            padding-bottom: 20px;

                            .aidsMaxAmount {
                                position: absolute;
                                width: 100%;
                                font-family: $font-family-text-bold;
                                text-align: center;
                                color: $c-grey-blue;
                                font-size: 1.3rem;
                                padding-top: 2px;
                                line-height: 1;
                            }
                        }

                        button.btn-actions {
                            display: block;
                            text-align: center;
                        }

                        
                        
                    }
                }
            }
        }
    }

    .SavingOptionsTable { 
        //Mobile max 768px
        @include breakpoint(only-md) {
            padding-bottom: 150px;

            .desktop-infos,
            .desktop-infos.container {
                display: none;
            }

            .mobile-title {
                font-size: 2.2rem;
                font-family: $font-family-text-bold;
                text-align: center;
            }
                
            .recommendations-table {
                
                &__t-head,
                &__t-body .t-row {
                    grid-template-rows: auto;
                    grid-template-columns: repeat(2, 1fr);
                }

                &__t-head > div {
                    padding: 5px 0;
                    text-decoration: underline;
                
                }

                &__t-body {
                    //box-shadow: 0px 25px 40px 0px #0C232C1A;

                    .t-row {
                        box-shadow: 0 25px 40px 0 rgb(12 35 44 / 10%);
                        border-radius: 10px;
                        margin: 20px 0;
                        padding: 30px 15px;

                        &__equipment-name {
                            grid-column: 1 / 3;
                            font-size: 1.6rem;
                            border-bottom: 1px solid $c-grey-5;
                        }

                        &__carbon-footprint {
                            font-size: 1.8rem;
                            margin: 15px 0;
                            padding-right: 15px !important;
                            border-right: 1px solid $c-grey-5;
                        }
                        &__potential-saving {
                            font-size: 1.8rem;
                            margin: 15px 0;
                            padding-left: 15px !important;
                        }
                        
                        &__actions {
                            grid-column: 1 / 3;
                            //justify-content: center;

                            .btn_container {   
                                padding-bottom: 30px;
                            }           

                            button.btn-actions {
                                width: 100%;
                                padding: 15px;
                            }
                        }

                        >div {
                            padding: 5px 0;
                        }
                    }
                }
            }
            
        }

        // Tablet 768px
        @include breakpoint(md) {
            display: flex;
            align-items: stretch;
            gap: 2.5vw;
            margin-bottom: 35px;
            padding: 15px 20px;
            box-shadow: 0px 25px 40px 0px #0C232C1A;


            .mobile-infos {
                display: none !important;
            }

            .current-consumption {
                flex-direction: column;
                justify-content: center;
                text-align: center;
                margin-bottom: 0;

                div.picto {
                    display: block;
                }

                &>div:nth-child(2) {
                    margin: 25px 0;
                }

                p {
                    font-size: 1.8rem;
                    max-width: 60%;
                    margin: 0 auto;
                }

                div {
                    font-size: 4rem;
                    line-height: 4.6rem;
                }
            }

            .recommendations-table {
                &__t-head,
                &__t-body .t-row {
                    text-align: start;
                    grid-template-columns: repeat(4, 1fr);
                }

                &__t-head > div,
                &__t-body .t-row > div {
                    &:nth-child(2),
                    &:nth-child(3) {
                        text-align: center;
                    }
                }

                &__t-body .t-row {
                    &:not(:last-child) {
                        border-bottom: 1px solid $c-grey-5;
                    }

                    &__actions {
                        font-size: 1.8rem;
                        justify-content: flex-end;
                        flex-wrap: wrap;
                    }
                }
            }
        }

        // Screen
        @include breakpoint(only-lg) {
            .recommendations-table__t-body .t-row__actions button.btn-actions {
                font-size: 1.4rem;
                line-height: 1.2;
            }
        }

        // Screen
        @include breakpoint(only-xl) {
            .recommendations-table__t-body .t-row__actions button.btn-actions {
                font-size: 1.6rem;
                line-height: 1.2;
            }
        }
    }

</style>